import { useEffect } from 'react';

interface SEOMetadata {
  title: string;
  description: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  canonicalUrl?: string;
  noindex?: boolean;
}

export const useSEO = (metadata: SEOMetadata) => {
  useEffect(() => {
    // Title
    document.title = metadata.title;
    updateOrCreateMeta('og:title', metadata.ogTitle || metadata.title);

    // Description
    updateOrCreateMeta('description', metadata.description);
    updateOrCreateMeta('og:description', metadata.ogDescription || metadata.description);

    // Keywords
    if (metadata.keywords) {
      updateOrCreateMeta('keywords', metadata.keywords);
    }

    // Open Graph
    updateOrCreateMeta('og:type', metadata.ogType || 'website');
    if (metadata.ogImage) {
      updateOrCreateMeta('og:image', metadata.ogImage);
    }

    // Canonical URL
    if (metadata.canonicalUrl) {
      updateOrCreateCanonical(metadata.canonicalUrl);
    }

    // No-index
    if (metadata.noindex) {
      updateOrCreateMeta('robots', 'noindex');
    } else {
      // Remove noindex if it exists
      const robotsMeta = document.querySelector('meta[name="robots"]');
      if (robotsMeta) {
        robotsMeta.remove();
      }
    }

    // Twitter Card
    updateOrCreateMeta('twitter:card', 'summary_large_image');
    updateOrCreateMeta('twitter:title', metadata.ogTitle || metadata.title);
    updateOrCreateMeta('twitter:description', metadata.ogDescription || metadata.description);
    if (metadata.ogImage) {
      updateOrCreateMeta('twitter:image', metadata.ogImage);
    }
  }, [metadata]);
};

const updateOrCreateMeta = (name: string, content: string) => {
  let meta = document.querySelector(`meta[name="${name}"], meta[property="${name}"]`) as HTMLMetaElement;

  if (!meta) {
    meta = document.createElement('meta');
    if (name.startsWith('og:')) {
      meta.setAttribute('property', name);
    } else {
      meta.setAttribute('name', name);
    }
    document.head.appendChild(meta);
  }

  meta.content = content;
};

const updateOrCreateCanonical = (url: string) => {
  let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;

  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.appendChild(canonical);
  }

  canonical.href = url;
};
